import type { RefObject } from 'react';
import { formatEuroCents } from '../lib/format';
import type { KartenPhase, Kartenzahlung } from './useKartenzahlung';

/**
 * 2026-10-15 (v0.17 Stufe 3): die Anzeige einer laufenden oder beendeten Kartenzahlung --
 * Warten, Erfolg, keine Antwort, Geld ohne Zuordnung. Für Sammelgast und Person dieselbe; was
 * „gescheitert" und „bereit" zeigen, entscheidet der jeweilige Dialog (andere Knöpfe).
 */
export default function KartenStatus({
  phase,
  terminalName,
  backButtonRef,
  onAbbrechen,
  onErneutNachfragen,
  onKeineAntwortAbbrechen,
  onZurueck,
}: {
  phase: KartenPhase;
  terminalName?: string;
  backButtonRef?: RefObject<HTMLButtonElement | null>;
  onAbbrechen: (z: Kartenzahlung) => void;
  onErneutNachfragen: (z: Kartenzahlung) => void;
  onKeineAntwortAbbrechen: () => void;
  onZurueck: () => void;
}) {
  if (phase.art === 'warten') {
    return (
      <div className="kiosk-card-status" role="status">
        <p className="kiosk-card-amount">{formatEuroCents(phase.zahlung.amountCents)}</p>
        <p>
          {phase.abgebrochen
            ? 'Wird abgebrochen … Ein Moment noch, falls die Karte gerade durchging.'
            : terminalName
              ? `Bitte Karte an „${terminalName}“ halten …`
              : 'Bitte Karte an das Terminal halten …'}
        </p>
        <div className="confirm-dialog-actions">
          {!phase.abgebrochen && (
            <button ref={backButtonRef} onClick={() => onAbbrechen(phase.zahlung)}>
              Abbrechen
            </button>
          )}
        </div>
      </div>
    );
  }
  if (phase.art === 'erfolg') {
    return (
      <div className="kiosk-card-status kiosk-card-status--ok" role="status">
        {phase.dochErhalten && <p>Zahlung doch erhalten.</p>}
        <p className="kiosk-card-amount">
          Zahlung erhalten: {formatEuroCents(phase.zahlung.amountCents)} (Karte)
        </p>
      </div>
    );
  }
  if (phase.art === 'keineAntwort') {
    return (
      <div className="kiosk-card-status" role="status">
        <p className="kiosk-card-amount">Keine Antwort vom Terminal</p>
        <p>Nichts ist verbucht, solange SumUp kein Ergebnis gemeldet hat.</p>
        <div className="confirm-dialog-actions">
          <button onClick={onKeineAntwortAbbrechen}>Abbrechen</button>
          <button className="primary-button" onClick={() => onErneutNachfragen(phase.zahlung)}>
            Erneut nachfragen
          </button>
        </div>
      </div>
    );
  }
  if (phase.art === 'ohneZuordnung') {
    return (
      <div className="kiosk-card-status" role="alert">
        <p className="kiosk-card-amount">
          Karte belastet: {formatEuroCents(phase.zahlung.amountCents)}
        </p>
        <p>
          Dieser Gast war inzwischen schon abgerechnet. Die Kartenzahlung ist festgehalten, aber
          keinem Gast zugeordnet -- bitte einem Admin melden.
        </p>
        <div className="confirm-dialog-actions">
          <button ref={backButtonRef} onClick={onZurueck}>
            Zurück
          </button>
        </div>
      </div>
    );
  }
  return null;
}
